import React from 'react';
import FloatingLabelInput from '../../../../components/floatingLabelInput';
import { formatPhoneNumber } from '../../../../features/utils';

const emptyOfficer = { firstName: '', lastName: '', email: '', cellNumber: '', nmls: '' };

/**
 * Assigned Loan Officers
 * ----------------------
 * A Business Development Team Lead does not originate loans, so instead of the
 * "Will this employee originate loans?" question the Loan Officer(s) working with the
 * team are listed here. The list lives inside the Team Lead / Co Team Lead modal and
 * is saved on that person's record (assigned_loan_officers).
 */
const AssignedLoanOfficers = ({
    officers = [],
    onChange,
    label = 'Assigned Loan Officers',
    description = 'Add the Loan Officer(s) that will be assigned to this team.',
}) => {

    const handleAdd = () => onChange([...officers, { ...emptyOfficer }]);

    const handleRemove = (index) => onChange(officers.filter((_, i) => i !== index));

    const handleFieldChange = (index, e) => {
        const { name, value } = e.target;
        const newValue = name === 'cellNumber' ? formatPhoneNumber(value) : value;
        onChange(officers.map((officer, i) => (i === index ? { ...officer, [name]: newValue } : officer)));
    };

    return (
        <section className="mt-10 mb-10 pt-8 border-t border-slate-200">
            <div className="flex items-center">
                <h3 className="text-lg font-semibold text-[#0a1445]">{label}</h3>
                <button
                    onClick={handleAdd}
                    className="ml-4 text-lg text-custom-purple"
                    type="button"
                >
                    <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 12h14m-7 7V5" />
                    </svg>
                </button>
            </div>

            <p className="mt-2 text-sm text-gray-500">{description}</p>

            {officers.map((officer, index) => (
                <div key={index} className="mt-6 p-4 bg-white shadow-inner rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-slate-700">Loan Officer {index + 1}</span>
                        <button
                            type="button"
                            onClick={() => handleRemove(index)}
                            className="text-red-600 px-2 py-1"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                            </svg>
                        </button>
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                            <FloatingLabelInput
                                handleChange={(e) => handleFieldChange(index, e)}
                                value={officer.firstName}
                                name="firstName"
                                id={`assigned_lo_first_name_${index}`}
                                label="First Name"
                                required={true}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <FloatingLabelInput
                                handleChange={(e) => handleFieldChange(index, e)}
                                value={officer.lastName}
                                name="lastName"
                                id={`assigned_lo_last_name_${index}`}
                                label="Last Name"
                                required={true}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <FloatingLabelInput
                                handleChange={(e) => handleFieldChange(index, e)}
                                value={officer.email}
                                name="email"
                                id={`assigned_lo_email_${index}`}
                                type="email"
                                label="Email"
                                required={true}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <FloatingLabelInput
                                handleChange={(e) => handleFieldChange(index, e)}
                                value={officer.cellNumber}
                                name="cellNumber"
                                id={`assigned_lo_cell_${index}`}
                                type="tel"
                                label="Cell Number"
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <FloatingLabelInput
                                handleChange={(e) => handleFieldChange(index, e)}
                                value={officer.nmls}
                                name="nmls"
                                id={`assigned_lo_nmls_${index}`}
                                label="NMLS #"
                            />
                        </div>
                    </div>
                </div>
            ))}
        </section>
    );
};

export default AssignedLoanOfficers;
